import React, { ReactElement, useCallback, useMemo, useState } from 'react';
import KubitContextualMenu from '../../../Dropdown/ContextualMenu';
import KubitTooltip from '../../../KubitTooltip';
import { NumberInputField } from '../../../Form';
import * as BuilderUI from '../../BuilderUI';
import {
  DirectionalHint,
  IContextualMenuItem,
  IContextualMenuStyles,
} from '@fluentui/react';
import styled from 'styled-components';
import { IDismissContextualMenuCallback } from '../../../Dropdown/ContextualMenu/KubitContextualMenu';
import { ITooltipOption } from '../../../KubitTooltip/globalTooltips';
import { NumericalMenuOption } from './types';
import { isNil } from 'lodash';
import { SvgIcon } from '../../../Icon';
import ContextualMenuOption from '../../../Dropdown/ContextualMenu/ContextualMenuOption';
import { SingleSelectLabel } from '../../../Dropdown/SingleSelect';
import { MAX_INTEGER } from '../../../../config/constants';

const NumberInput = styled(NumberInputField)<{ $width: number }>`
  width: ${({ $width }) => $width}px;
  > input {
    width: 100%;
  }
`;

const SaveIcon = styled(SvgIcon)`
  position: absolute;
  right: 4px;
  top: 50%;
  cursor: pointer;
  transform: translateY(-50%);
`;

interface IProps {
  value: number | null;
  max?: number;
  min?: number;
  options: NumericalMenuOption[];
  suffix?: string;
  label?: string;
  className?: string;
  placeholder?: string;
  tooltipContent?: ITooltipOption;
  directionalHint?: DirectionalHint;
  menuWidth?: number;
  trigger?: ReactElement;
  onChange: (newValue: number) => void;
  precision?: number;
}

const BuilderNumericalMenu = ({
  value,
  max = MAX_INTEGER,
  min = 0,
  options,
  suffix,
  label,
  className,
  placeholder,
  tooltipContent,
  directionalHint = DirectionalHint.bottomRightEdge,
  menuWidth = 120,
  trigger,
  onChange,
  precision = 0,
}: IProps) => {
  const numericalStyles: Partial<IContextualMenuStyles> = {
    root: {
      minWidth: `${menuWidth}px`,
    },
  };

  const [numberInput, setNumberInput] = useState<number | null>(null);

  const handleSubmit = useCallback(
    (newValue: number | null) => {
      setNumberInput(null);
      if (!isNil(newValue) && newValue <= max) {
        onChange(newValue);
      }
    },
    [onChange, max],
  );

  const handleSubmitCustomValue = useCallback(
    (
      customValue: number | null,
      dismissMenu: IDismissContextualMenuCallback,
    ) => {
      handleSubmit(customValue);
      dismissMenu(undefined, true);
    },
    [handleSubmit],
  );

  const menuItems: IContextualMenuItem[] = useMemo(
    () => [
      {
        key: 'numberInput',
        onRender: (_, dismissMenu) => (
          <NumberInput
            precision={precision}
            $width={menuWidth}
            placeholder={placeholder}
            max={max}
            min={min}
            value={numberInput}
            onChange={setNumberInput}
            suffixAction={
              !isNil(numberInput) && (
                <SaveIcon
                  name="circle-check"
                  width={14}
                  height={14}
                  onClick={() =>
                    handleSubmitCustomValue(numberInput, dismissMenu)
                  }
                  isHoverable={true}
                />
              )
            }
          />
        ),
      },
      ...options.map((option) => {
        return {
          key: `option_${option.value}`,
          onRenderContent: () => (
            <ContextualMenuOption
              label={option.text}
              onClick={() => {
                handleSubmit(option.value);
              }}
            />
          ),
        };
      }),
    ],
    [
      precision,
      numberInput,
      handleSubmitCustomValue,
      handleSubmit,
      max,
      min,
      options,
      placeholder,
      menuWidth,
    ],
  );

  const triggerDisplay = (
    <BuilderUI.BuilderFieldContainer className={className}>
      {label && (
        <KubitTooltip {...tooltipContent} noOpenDelay={true}>
          <BuilderUI.BuilderFieldLabel>{label}</BuilderUI.BuilderFieldLabel>
        </KubitTooltip>
      )}
      {trigger ?? (
        <KubitTooltip {...(label ? {} : tooltipContent)} noOpenDelay={true}>
          <SingleSelectLabel isAlwaysActive={true}>
            {value}
            {suffix || ''}
          </SingleSelectLabel>
        </KubitTooltip>
      )}
    </BuilderUI.BuilderFieldContainer>
  );

  return (
    /* Wrapped in div because KubitContextualMenu renders additional DOM element when
     menu is shown which causes extra gap if used in flex box with gap */
    <div>
      <KubitContextualMenu
        items={menuItems}
        trigger={triggerDisplay}
        style={numericalStyles}
        directionalHint={directionalHint}
      />
    </div>
  );
};

export default BuilderNumericalMenu;
